import React, { useRef, useCallback } from 'react';
import { TabList as AriaTabList } from 'react-aria-components';
import { cva, type VariantProps } from 'class-variance-authority';
import { CarouselButton } from '../Carousel/CarouselButton/CarouselButton';
import { useTabsContext } from './Tabs';
import { useCarousel } from '../../utils/carousel/hooks/useCarousel';
import styles from './Tabs.module.css';

const tabList = cva(styles.tabs__list, {
  variants: {
    fillContainer: {
      true: styles['tabs__list--fill-container'],
      false: undefined,
    },
  },
  defaultVariants: {
    fillContainer: false,
  },
});

export interface TabListProps extends VariantProps<typeof tabList> {
  /**
   * Tab components to render
   */
  children: React.ReactNode;
  /**
   * Additional CSS class names
   * @ignore
   */
  className?: string;
  /**
   * Test ID for the component, used for testing purposes
   */
  'data-testid'?: string;
}

export const TabList: React.FC<TabListProps> = ({
  children,
  className,
  'data-testid': dataTestId,
}) => {
  const context = useTabsContext();
  const { styleVariant, align, fillContainer, focusStyle } = context;

  const containerRef = useRef<HTMLDivElement>(null);

  // Get arrow variant based on style variant
  const arrowVariant: 'shapeElevated' | 'gradient' =
    styleVariant === 'contained' ? 'shapeElevated' : 'gradient';

  const {
    scrollerRef,
    setScrollerRef,
    canScrollLeft,
    canScrollRight,
    isOverflowing,
    isItemFullyVisible,
    scrollToItem,
    prevArrowProps,
    nextArrowProps,
    arrowsVisible,
    dragProps,
  } = useCarousel({
    arrowStyleVariant: arrowVariant,
    focusStyle,
    mouseDragging: false,
    hideDisabledArrows: true,
  });

  // Cast scrollerRef to the HTMLDivElement type AriaTabList expects
  const tabListRef = scrollerRef as React.RefObject<HTMLDivElement>;

  // Whether fillContainer suppresses scroll buttons
  const showScrollButtons = !fillContainer && isOverflowing && arrowsVisible;

  const itemsLength = React.Children.count(children);

  const scrollToLeft = useCallback(() => {
    if (!tabListRef.current || !canScrollLeft) return;
    const el = tabListRef.current;
    if (!isItemFullyVisible(0)) {
      scrollToItem(0);
    } else {
      el.scrollBy({ left: -(el.clientWidth * 0.6), behavior: 'smooth' });
    }
  }, [canScrollLeft, isItemFullyVisible, scrollToItem, tabListRef]);

  const scrollToRight = useCallback(() => {
    if (!tabListRef.current || !canScrollRight) return;
    const el = tabListRef.current;
    if (!isItemFullyVisible(itemsLength - 1)) {
      scrollToItem(itemsLength - 1);
    } else {
      el.scrollBy({ left: el.clientWidth * 0.6, behavior: 'smooth' });
    }
  }, [
    canScrollRight,
    isItemFullyVisible,
    itemsLength,
    scrollToItem,
    tabListRef,
  ]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const baseClassName = [
    tabList({ fillContainer }),
    !fillContainer && styles['tabs__list--scrollable'],
  ]
    .filter(Boolean)
    .join(' ');
  const combinedClassName = className
    ? `${baseClassName} ${className}`
    : baseClassName;

  return (
    <div
      className={styles['tabs__list-container']}
      ref={containerRef}
      data-has-scroll-buttons={showScrollButtons ? 'true' : undefined}
      data-style-variant={styleVariant}
      data-testid={dataTestId}
    >
      {showScrollButtons && !prevArrowProps.hidden && (
        <CarouselButton
          dir="prev"
          variant={prevArrowProps.styleVariant}
          size={prevArrowProps.size}
          focusStyle={prevArrowProps.focusStyle}
          hideDisabledArrow={prevArrowProps.hidden}
          isDisabled={prevArrowProps.disabled}
          className={styles['tabs__scroll-button--prev']}
          onClick={scrollToLeft}
          standalone={true}
        />
      )}

      <AriaTabList
        ref={setScrollerRef as React.RefCallback<HTMLDivElement>}
        className={combinedClassName}
        data-style-variant={styleVariant}
        data-align={align}
        {...dragProps}
      >
        {children}
      </AriaTabList>

      {showScrollButtons && !nextArrowProps.hidden && (
        <CarouselButton
          dir="next"
          variant={nextArrowProps.styleVariant}
          size={nextArrowProps.size}
          focusStyle={nextArrowProps.focusStyle}
          hideDisabledArrow={nextArrowProps.hidden}
          isDisabled={nextArrowProps.disabled}
          className={styles['tabs__scroll-button--next']}
          onClick={scrollToRight}
          standalone={true}
        />
      )}
    </div>
  );
};

export default TabList;
